import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TaskList from './components/TaskList';
import TaskForm from './components/TaskForm';
import FilterBar from './components/FilterBar';
import StatsCard from './components/StatsCard';
import Header from './components/Header';
import PasswordGate from './components/PasswordGate';
import AccessDenied from './components/AccessDenied';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';

const { FiPlus, FiCheck, FiClock, FiTrendingUp, FiTarget } = FiIcons;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  useEffect(() => {
    const savedTasks = localStorage.getItem('tasks');
    if (savedTasks) setTasks(JSON.parse(savedTasks));
    
    // Check if user was already authenticated in this session
    const authStatus = sessionStorage.getItem('is_auth');
    if (authStatus === 'true') setIsAuthenticated(true);
  }, []);

  useEffect(() => {
    localStorage.setItem('tasks', JSON.stringify(tasks));
  }, [tasks]);

  const handleVerify = () => {
    setIsAuthenticated(true);
    setAccessDenied(false);
    sessionStorage.setItem('is_auth', 'true');
  };

  const handleDenied = () => {
    setAccessDenied(true);
  };

  const handleRetry = () => {
    setAccessDenied(false);
  };

  const addTask = (taskData) => {
    const newTask = {
      id: Date.now(),
      ...taskData,
      createdAt: new Date().toISOString(),
      completed: false
    };
    setTasks(prev => [...prev, newTask]);
    setShowForm(false);
  };

  const updateTask = (taskData) => {
    setTasks(prev => prev.map(task => 
      task.id === editingTask.id ? { ...task, ...taskData } : task
    ));
    setEditingTask(null);
    setShowForm(false);
  };

  const deleteTask = (id) => setTasks(prev => prev.filter(task => task.id !== id));
  
  const toggleTask = (id) => {
    setTasks(prev => prev.map(task =>
      task.id === id ? { ...task, completed: !task.completed } : task
    ));
  };

  const filteredTasks = tasks.filter(task => {
    switch (filter) {
      case 'active': return !task.completed;
      case 'completed': return task.completed;
      case 'high': return task.priority === 'high' && !task.completed;
      default: return true;
    }
  });

  const stats = {
    total: tasks.length,
    completed: tasks.filter(t => t.completed).length,
    active: tasks.filter(t => !t.completed).length,
    highPriority: tasks.filter(t => t.priority === 'high' && !t.completed).length
  };

  if (accessDenied) {
    return <AccessDenied onRetry={handleRetry} />;
  }

  if (!isAuthenticated) {
    return <PasswordGate onVerify={handleVerify} onDenied={handleDenied} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <Header />
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatsCard title="Total Tasks" value={stats.total} icon={FiTarget} color="bg-gradient-to-r from-blue-500 to-blue-600" />
          <StatsCard title="Completed" value={stats.completed} icon={FiCheck} color="bg-gradient-to-r from-green-500 to-green-600" />
          <StatsCard title="Active" value={stats.active} icon={FiClock} color="bg-gradient-to-r from-orange-500 to-orange-600" />
          <StatsCard title="High Priority" value={stats.highPriority} icon={FiTrendingUp} color="bg-gradient-to-r from-red-500 to-red-600" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-800">Tasks</h2>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowForm(true)}
                    className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2 rounded-xl font-medium shadow-lg transition-all duration-200"
                  >
                    <SafeIcon icon={FiPlus} className="text-lg" />
                    Add Task
                  </motion.button>
                </div>
                <FilterBar filter={filter} setFilter={setFilter} />
              </div>
              <div className="p-6">
                <TaskList tasks={filteredTasks} onToggle={toggleTask} onDelete={deleteTask} onEdit={(t) => { setEditingTask(t); setShowForm(true); }} />
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <AnimatePresence>
              {showForm && (
                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden sticky top-8">
                  <TaskForm onSubmit={editingTask ? updateTask : addTask} onClose={() => { setShowForm(false); setEditingTask(null); }} initialData={editingTask} isEditing={!!editingTask} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;